import { FC, useRef, useState } from 'react';

import {
    Box,
    FormControl,
    FormHelperText,
    FormLabel,
    Grid,
    MenuItem,
    Select,
    styled,
    Typography,
} from '@mui/material';
import { CommissionFrequency } from '@prisma/client';
import { Formik } from 'formik';
import { UKBankDetails } from 'types/graphql';

import { SortCodeAccountNumberShape } from 'src/components/SortCodeAccountInput/SortCodeAccount.validation';
import { showErrorToast } from 'src/components/Toasts/toast';
import { DEFAULT_BORDER } from 'src/constants';
import { useAddPartnerContext } from 'src/contexts/AddPartnerProvider';
import { useBankValidation } from 'src/hooks';
import { isIreland } from 'src/lib/countryConfig';

import { NavigationButtons } from '../components/NavigationButtons';
import { addPartnerStep3Validation } from '../validation/addPartnerStep3';

import GBBankDetails, { GBBankDetailsValues } from './BankDetails/GBBankDetails';
import IEBankDetails, { IEBankDetailsValues } from './BankDetails/IEBankDetails';

const MOCK_COMMISION_FREQUENCY = ['WEEKLY', 'MONTHLY'];

const Step3: FC = () => {
    const { validateIBANAndBIC } = useBankValidation();

    const { application, addPartner, activeStep, handleBack } = useAddPartnerContext();

    const [UKBankDetailsResult, setUKBankDetailsResult] = useState<UKBankDetails>();
    const [validatingBankDetails, setValidatingBankDetails] = useState(false);

    const lastSortCodeValue = useRef<string>('');

    const ADD_PAYMENT_METHOD_INITIAL_VALUES: IEBankDetailsValues | GBBankDetailsValues = isIreland
        ? {
              bicNumber: application?.bicNumber || '',
              ibanNumber: application?.ibanNumber || '',
              commissionFrequency: application?.commissionFrequency || CommissionFrequency.WEEKLY,
          }
        : {
              sortCode: application?.sortCode || '',
              accountNumber: application?.accountNumber || '',
              ibanNumber: application?.ibanNumber || '',
              commissionFrequency: application?.commissionFrequency || CommissionFrequency.WEEKLY,
          };

    const validationSchema = isIreland ? addPartnerStep3Validation : SortCodeAccountNumberShape;

    const Form = styled('form')({
        width: '100%',
    });

    const showValidatingBankDetailsError = () => {
        if (validatingBankDetails) {
            showErrorToast('Still validating bank details, please wait...');
            return;
        }
    };

    return (
        <Grid container>
            <Grid
                item
                xs={12}
                sx={{
                    height: '116px',
                    border: '1px solid #F3A256',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                    borderRadius: '16px',
                    padding: '8px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    marginBottom: '24px',
                }}
            >
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#1D1D1B' }}>
                    Payment Information
                </Typography>
                <Typography>
                    This account will be used for invoice payments and commission payments
                </Typography>
            </Grid>

            <Formik
                initialValues={ADD_PAYMENT_METHOD_INITIAL_VALUES}
                validationSchema={validationSchema}
                onSubmit={async (values, { setErrors }) => {
                    let formattedValues = values;
                    if (isIreland) {
                        formattedValues = {
                            ...values,
                            ibanNumber: (values as IEBankDetailsValues).ibanNumber?.toUpperCase(),
                            bicNumber: (values as IEBankDetailsValues).bicNumber?.toUpperCase(),
                        };
                        const errors = await validateIBANAndBIC(
                            (formattedValues as IEBankDetailsValues).ibanNumber,
                            (formattedValues as IEBankDetailsValues).bicNumber,
                            'ibanNumber',
                            'bicNumber'
                        );

                        if (Object.keys(errors).length > 0) {
                            setErrors(errors);
                            return;
                        }
                    } else {
                        showValidatingBankDetailsError();

                        switch (UKBankDetailsResult?.StatusInformation) {
                            case 'InvalidAccountNumber':
                                setErrors({
                                    accountNumber: 'Invalid account number',
                                });
                                return;
                            case 'UnknownSortCode':
                                setErrors({
                                    sortCode: 'Invalid sort code',
                                });
                                return;
                        }

                        if (!formattedValues.ibanNumber) {
                            return;
                        }
                    }

                    await addPartner({ ...formattedValues, step: 3 }, activeStep);
                }}
                enableReinitialize={false}
            >
                {(formikProps) => {
                    const {
                        values,
                        setFieldValue,
                        errors,
                        handleChange,
                        touched,
                        handleBlur,
                        handleSubmit,
                    } = formikProps;

                    return (
                        <Form>
                            {/** Bank details */}
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    ...DEFAULT_BORDER,
                                    backgroundColor: '#FFFFFF',
                                    boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                                    padding: '32px 24px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    gap: '24px',
                                    marginBottom: '24px',
                                }}
                                container
                            >
                                <Grid item xs={12} sx={{ borderBottom: '1px solid #F18F34' }}>
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap="8px"
                                        marginBottom="8px"
                                    >
                                        <Typography variant="h2">Bank Details</Typography>
                                    </Box>
                                </Grid>

                                {isIreland ? (
                                    <IEBankDetails
                                        values={values as IEBankDetailsValues}
                                        errors={errors as IEBankDetailsValues}
                                        handleChange={handleChange}
                                        application={application}
                                    />
                                ) : (
                                    <GBBankDetails
                                        values={values as GBBankDetailsValues}
                                        setFieldValue={setFieldValue}
                                        handleChange={handleChange}
                                        application={application}
                                        setBankValidationResult={(res) => {
                                            setUKBankDetailsResult(res);
                                            setFieldValue('ibanNumber', res?.IBAN || '');
                                            setValidatingBankDetails(false);
                                        }}
                                        setValidatingBankDetails={setValidatingBankDetails}
                                        setLastSortCodeValue={(value) => {
                                            return (lastSortCodeValue.current = value);
                                        }}
                                    />
                                )}
                            </Grid>

                            {/** Commission Generation */}
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    ...DEFAULT_BORDER,
                                    backgroundColor: '#FFFFFF',
                                    boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                                    padding: '32px 24px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    gap: '24px',
                                }}
                                container
                            >
                                <Grid item xs={12} sx={{ borderBottom: '1px solid #F18F34' }}>
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap="8px"
                                        marginBottom="8px"
                                    >
                                        <Typography variant="h2">Commission Generation</Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} spacing={2} container>
                                    <Grid item xs={12} lg={6}>
                                        <FormControl fullWidth>
                                            <FormLabel
                                                sx={{
                                                    marginBottom: '8px',
                                                    color: '#000',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Choose the Partner&apos;s commission statement
                                                frequency:
                                            </FormLabel>
                                            <Select
                                                inputProps={{
                                                    'data-testid': `commissionFrequency-input`,
                                                }}
                                                labelId="commissionFrequency"
                                                id="commissionFrequency"
                                                name="commissionFrequency"
                                                value={values.commissionFrequency}
                                                onChange={(event) => {
                                                    const value = event.target
                                                        .value as keyof typeof CommissionFrequency;
                                                    setFieldValue(
                                                        'commissionFrequency',
                                                        CommissionFrequency[value]
                                                    );
                                                }}
                                                error={
                                                    touched.commissionFrequency &&
                                                    Boolean(errors.commissionFrequency)
                                                }
                                                onBlur={handleBlur}
                                            >
                                                {MOCK_COMMISION_FREQUENCY.map((hierarchy) => {
                                                    return (
                                                        <MenuItem key={hierarchy} value={hierarchy}>
                                                            {hierarchy}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                            <FormHelperText>
                                                {touched.commissionFrequency &&
                                                    errors.commissionFrequency}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                <NavigationButtons
                                    onBack={handleBack}
                                    onNext={(event) => {
                                        event.preventDefault();
                                        if (!isIreland) {
                                            if (
                                                lastSortCodeValue.current !== '' &&
                                                application?.sortCode &&
                                                lastSortCodeValue.current !== application?.sortCode
                                            ) {
                                                const success = ['OK', 'CautiousOK'].includes(
                                                    UKBankDetailsResult?.StatusInformation
                                                );

                                                if (!success) {
                                                    showErrorToast(
                                                        'Sort Code has changed, please validate again.'
                                                    );
                                                    return;
                                                }
                                            }

                                            showValidatingBankDetailsError();
                                        }

                                        return handleSubmit();
                                    }}
                                />
                            </Grid>
                        </Form>
                    );
                }}
            </Formik>
        </Grid>
    );
};

export default Step3;
